import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CardName,
  getRandomTarotCard,
  preloadCardImage,
} from "@/lib/constants/tarot";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import CardImage from "../CardImage";
import { Button } from "../ui/button";

function DemoPage() {
  const { t } = useLanguage();
  const [demoCard, setDemoCard] = useState<CardName | null>(null);
  const [isFlipping, setIsFlipping] = useState(false);

  const drawDemoCard = async () => {
    setIsFlipping(true);

    // Get the card and preload its image immediately
    const selectedCard = getRandomTarotCard();

    try {
      // Preload the image during the first half of the animation
      await preloadCardImage(selectedCard);
    } catch (error) {
      console.warn("Failed to preload card image:", error);
    }

    // Set the card at the middle of the flip animation (1 second)
    setTimeout(() => {
      setDemoCard(selectedCard);

      // Reset flip state after card is revealed (another 1 second)
      setTimeout(() => {
        setIsFlipping(false);
      }, 1000);
    }, 1000);
  };

  const resetDemoCard = () => {
    setDemoCard(null);
    setIsFlipping(false);
  };

  return (
    <div className="demo-container flex-1 flex flex-col p-4">
      <div className="demo-content text-center w-full max-w-sm sm:max-w-lg lg:max-w-4xl mx-auto flex-1 flex flex-col">
        <div className="demo-header mb-6">
          <h2 className="demo-page-title text-xl sm:text-2xl font-light">
            {t("demoTitle")}
          </h2>
        </div>

        <div className="component-showcase-area bg-gray-50 border-2 border-black p-4 sm:p-8 lg:p-12 text-left mx-2 sm:mx-0 flex-1 overflow-y-auto">
          <h3 className="showcase-title text-lg sm:text-xl font-medium mb-4">
            {t("componentShowcase")}
          </h3>

          <div className="showcase-sections space-y-4 sm:space-y-6">
            <div className="buttons-section space-y-2">
              <h4 className="section-title font-medium text-sm sm:text-base">
                {t("buttonsSection")}
              </h4>
              <div className="button-examples flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  className="demo-btn-outline border-2 border-black hover:bg-black hover:text-white text-sm sm:text-base"
                >
                  {t("outlineButton")}
                </Button>
                <Button className="demo-btn-filled bg-black text-white hover:bg-gray-800 text-sm sm:text-base">
                  {t("filledButton")}
                </Button>
              </div>
            </div>

            <div className="typography-section space-y-2">
              <h4 className="section-title font-medium text-sm sm:text-base">
                {t("typographySection")}
              </h4>
              <div className="typography-examples space-y-1">
                <p className="demo-brand-example text-2xl sm:text-3xl lg:text-4xl font-light tracking-wider">
                  {t("brandName")}
                </p>
                <p className="demo-heading-2 text-lg sm:text-xl lg:text-2xl font-light">
                  {t("heading2")}
                </p>
                <p className="demo-body-text text-base sm:text-lg">
                  {t("bodyText")}
                </p>
                <p className="demo-secondary-text text-sm text-gray-600">
                  {t("secondaryText")}
                </p>
              </div>
            </div>

            <div className="card-section space-y-2">
              <h4 className="section-title font-medium text-sm sm:text-base">
                Card Animation Demo
              </h4>
              <div className="demo-card-area space-y-4">
                <div className="card-demo-container flex items-center justify-center p-6 bg-white border border-gray-300">
                  {!demoCard ? (
                    <div
                      onClick={drawDemoCard}
                      className="card-flip-container transition-transform duration-2000 cursor-pointer"
                      style={{
                        transformStyle: "preserve-3d",
                        transform: isFlipping
                          ? "rotateY(180deg)"
                          : "rotateY(0deg)",
                      }}
                    >
                      <CardImage
                        cardName={null}
                        className="w-20 h-32 sm:w-24 sm:h-36 rounded-lg shadow-lg"
                        alt="Demo card back"
                      />
                    </div>
                  ) : (
                    <CardImage
                      cardName={demoCard}
                      className="w-20 h-32 sm:w-24 sm:h-36 rounded-lg shadow-lg"
                      alt={demoCard}
                    />
                  )}
                </div>
                <div className="demo-controls flex gap-2 justify-center">
                  <Button
                    onClick={drawDemoCard}
                    variant="outline"
                    size="sm"
                    disabled={isFlipping}
                    className="border-black hover:bg-black hover:text-white"
                  >
                    {isFlipping ? "Flipping..." : "Draw Card"}
                  </Button>
                  <Button
                    onClick={resetDemoCard}
                    variant="outline"
                    size="sm"
                    className="border-gray-400 hover:bg-gray-400 hover:text-white"
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </div>

            <div className="animation-workflow-section space-y-2">
              <h4 className="section-title font-medium text-sm sm:text-base">
                Animation Workflow System
              </h4>
              <p className="text-xs sm:text-sm text-gray-600 mb-3">
                Advanced animation system with chaining, configurable options,
                and callbacks
              </p>
              <Link to="/demo/animation-workflow">
                <Button
                  variant="outline"
                  className="border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white text-sm"
                >
                  View Animation Demos →
                </Button>
              </Link>
            </div>

            <div className="layout-section space-y-2">
              <h4 className="section-title font-medium text-sm sm:text-base">
                {t("layoutSection")}
              </h4>
              <div className="layout-grid grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                <div className="grid-box-1 bg-white border-2 border-black p-2 sm:p-4 text-center text-sm sm:text-base">
                  Box 1
                </div>
                <div className="grid-box-2 bg-white border-2 border-black p-2 sm:p-4 text-center text-sm sm:text-base">
                  Box 2
                </div>
                <div className="grid-box-3 bg-white border-2 border-black p-2 sm:p-4 text-center text-sm sm:text-base">
                  Box 3
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DemoPage;
